/**
 * Shared themed button atoms for the generic-CRUD admin pages (OIDCPAM
 * Gate-6 Phase 4a). Used by CrudForm's submit/Add buttons and by each CRUD
 * page's row actions (Edit/Rotate/Delete/Cancel/Copy/Dismiss) so no page
 * needs to hand-roll its own button styling — mirrors the
 * `PrimaryButton`/`SecondaryButton`/`DangerButton` idiom already established
 * by `@devnw/cyber-iam`'s internal styledShared and this app's
 * `pages/setup/setupStyled.ts` (see that file's header comment: cyber-iam's
 * styledShared is NOT a public API, so equivalents are re-derived here from
 * the public `@devnw/theme` tokens).
 *
 * All colors/spacing/radius come from `asUnified(theme)`/`ut(...)` — no
 * hardcoded values except literal fallbacks for when a theme field is
 * optional-chained (same defensive pattern as PamStateComponents.tsx /
 * AdminLayout.tsx, so these buttons never throw even without a
 * ThemeProvider).
 */
import styled, { css } from 'styled-components'
import { asUnified, ut } from '@devnw/theme'

const buttonBase = css`
  min-height: 44px;
  padding: ${({ theme }) =>
    `${ut(asUnified(theme)).space('xs')} ${ut(asUnified(theme)).space('md')}`};
  border-radius: ${({ theme }) => ut(asUnified(theme)).radius() ?? 4}px;
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.2s ease,
    opacity 0.2s ease;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

/** Filled primary action button (form submit, primary page actions). */
export const CrudPrimaryButton = styled.button`
  ${buttonBase}
  border: none;
  background: ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
  color: ${({ theme }) => asUnified(theme).text?.inverse ?? '#ffffff'};

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`

/** Outlined secondary action button (Edit/Cancel/Rotate/Copy/Dismiss/Add). */
export const CrudSecondaryButton = styled.button`
  ${buttonBase}
  border: 1px solid ${({ theme }) => ut(asUnified(theme)).borderColor() ?? '#cbd5e1'};
  background: none;
  color: ${({ theme }) => asUnified(theme).text?.primary ?? '#0f172a'};

  &:hover:not(:disabled) {
    background: ${({ theme }) => asUnified(theme).bg?.canvas ?? '#f8fafc'};
  }
`

/** Outlined destructive action button (Delete/Remove). */
export const CrudDangerButton = styled.button`
  ${buttonBase}
  border: 1px solid ${({ theme }) => asUnified(theme).colors?.danger ?? '#991b1b'};
  background: none;
  color: ${({ theme }) => asUnified(theme).colors?.danger ?? '#991b1b'};

  &:hover:not(:disabled) {
    background: ${({ theme }) => `${asUnified(theme).colors?.danger ?? '#991b1b'}14`};
  }
`

/** Wrapping horizontal row of row-action buttons (used inside a CrudTable cell). */
export const CrudActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => ut(asUnified(theme)).space('xs')};
`
